import React from 'react';
import { FileWarning, Loader2 } from 'lucide-react';

import { cn } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

interface PdfCanvasPreviewProps {
  data: Uint8Array;
  fileName: string;
  variant: 'inline' | 'modal' | 'thumbnail';
}

// Gross format screening only. Native viewers own parsing and recovery, including
// prefixed headers, damaged xrefs, missing EOF markers, and trailing transport data.
const hasPdfSignature = (data: Uint8Array): boolean => {
  for (let offset = data.indexOf(0x25); offset !== -1 && offset + 5 < data.length;
    offset = data.indexOf(0x25, offset + 1)) {
    if (data[offset + 1] === 0x50 && data[offset + 2] === 0x44
      && data[offset + 3] === 0x46 && data[offset + 4] === 0x2d) return true;
  }
  return false;
};

export const PdfCanvasPreview: React.FC<PdfCanvasPreviewProps> = ({
  data,
  fileName,
  variant,
}) => {
  const { t } = useSettings();
  const [resolved, setResolved] = React.useState<{ data: Uint8Array; url: string | null } | null>(null);
  const current = resolved?.data === data ? resolved : null;
  const previewUrl = current?.url;

  React.useEffect(() => {
    let objectUrl: string | null = null;
    try {
      if (!hasPdfSignature(data)) throw new Error('Missing PDF signature');
      const copiedData = new Uint8Array(new ArrayBuffer(data.byteLength));
      copiedData.set(data);
      const pdfBlob = new Blob([copiedData.buffer], { type: 'application/pdf' });
      objectUrl = URL.createObjectURL(pdfBlob);
      setResolved({ data, url: objectUrl });
    } catch {
      setResolved({ data, url: null });
    }

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [data]);

  if (current && !previewUrl) {
    return (
      <div
        className="flex h-full min-h-36 flex-col items-center justify-center gap-2 p-4 text-center text-sm text-gray-600 dark:text-notion-text-secondary"
        role="alert"
      >
        <FileWarning className="h-5 w-5 opacity-70" aria-hidden="true" />
        <span className="font-medium">{t('PDF preview unavailable')}</span>
        <span>{t('Download remains available from the file controls.')}</span>
      </div>
    );
  }

  if (!previewUrl) {
    return (
      <div
        className="flex h-full min-h-36 items-center justify-center gap-2 text-sm text-gray-600 dark:text-notion-text-secondary"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>{t('Loading PDF preview…')}</span>
      </div>
    );
  }

  return (
    <div
      className="flex h-full min-h-0 flex-col bg-white dark:bg-notion-bg-secondary"
      data-testid="attachment-pdf-native-preview"
      data-preview-variant={variant}
    >
      <iframe
        src={previewUrl}
        title={t('Preview of {{fileName}}', { fileName })}
        className={cn('block h-full min-h-36 w-full border-0 bg-white', variant === 'thumbnail' && 'min-h-0')}
      />
    </div>
  );
};
